import queryString from 'query-string';
import morphdom from 'morphdom';

import PageComponent from '../component/page-component';
import {getScrollTop} from '../utils/get-scroll';

const searchControlsAttr = 'searchControls';
const searchResultsAttr = 'searchResults';

const defaultValues = {
	// searchOnType: true,
	// searchOnEmpty: true,
	handleQueryString: false,
	scrollUpOnResults: true,
	scrollUpOffset: 100,
	actionAttribute: 'searchAction',
	busyClass: 'busy',
};

class Search extends PageComponent {
	constructor({element, root, defaults = {}}) {
		super({element: element, root: root});
		this.defaults = Object.assign({}, defaultValues, defaults);
		this.promise = Promise.resolve();
		this.urlChanged = false;
		this.queryString = queryString;
		this.searchCount = 0;
	}

	injectSearchEngineFactory(searchEngineFactory) {
		this.searchEngineFactory = searchEngineFactory;
	}

	injectSearchEngine(searchEngine) {
		this.searchEngine = searchEngine;
	}

	injectHistory(history) {
		this.history = history;
	}

	prepare() {
		this.options = this.dataAttr().getAll();
		const opts = this.options;
		this.params = opts.params || {};
		this.extraParams = opts.extraParams || {};
		this.searchControls = this.element.querySelector(this.dataSelector(searchControlsAttr));
		this.searchResults = this.element.querySelector(this.dataSelector(searchResultsAttr));

		if ('engineName' in opts) {
			if (opts.handleQueryString) {
				const len = location.search.length + location.hash.length;
				this.baseUrl = len ? location.href.slice(0, -len) : location.href;
			}
			const engineParams = opts.engineParams || {};
			this.engine = this.searchEngineFactory.newInstance(opts.engineName, engineParams);
			this.listeners.fieldChange = this.events.on(this.element, 'field:change', this.onFieldChange.bind(this));
			this.listeners.actions = this.events.on(this.element, this.dataSelector(opts.actionAttribute), 'click', this.onSearchAction.bind(this));
		}
	}

	onFieldChange(event) {
		this.params.page = 1;
		this.submit();
	}

	onChange(event) {
		if (this.waitTimeout) {
			clearTimeout(this.waitTimeout);
		}
		this.waitTimeout = setTimeout(this.submit.bind(this), this.options.waitTime);
	}

	onSearchAction(event, target) {
		event.preventDefault();
		const actionData = this.dataAttr(target).getAll();
		const action = this.options.actionAttribute in actionData ? actionData[this.options.actionAttribute] : '';
		delete actionData[this.options.actionAttribute];
		const method = action + 'Action';
		if (this[method] && this[method] instanceof Function) {
			this[method](actionData);
		}
	}

	async goToAction(params) {
		this.params.page = params.page;
		await this.submit();
		if (this.options.scrollUpOnResults) {
			const rect = this.searchResults.getBoundingClientRect();
			if (rect.top < 0) {
				const pos = rect.top + getScrollTop();
				window.scrollTo({
					left: 0,
					top: pos - this.options.scrollUpOffset,
					behavior: 'smooth',
				});
			}
		}
	}

	async resetAllAction(params) {
		const fields = [];
		const searchField = this.queryComponent(this.dataSelector('field', 'search'));
		if (searchField) {
			fields.push(searchField);
		}
		if ('facets' in this.params) {
			for (const facetName of Object.keys(this.params.facets)) {
				const field = this.queryComponent(this.dataSelector('field', facetName));
				if (field) {
					fields.push(field);
				}
			}
		}

		if (fields.length) {
			for (const field of fields) {
				if (field.reset && field.reset instanceof Function) {
					field.reset(false);
				}
			}
			this.params.page = 1;
			await this.submit();
		}
	}

	async submit() {
		const opts = this.options;
		const params = this.prepareParams();
		this.classList(this.element).add(opts.busyClass);
		try {
			const results = await this.engine.search(params);
			this.processResults(results);
		} catch (error) {
			console.log('search error');
			console.log(error);
		}
	}

	prepareParams() {
		this.searchCount++;
		const searchField = this.queryComponent(this.dataSelector('field', 'search'));
		if (searchField) {
			this.params.search = searchField.getValue();
		}
		if ('facets' in this.params) {
			for (const facetName of Object.keys(this.params.facets)) {
				const field = this.queryComponent(this.dataSelector('field', facetName));
				if (field) {
					const values = field.getValue();
					this.params.facets[facetName].values = Array.isArray(values) ? values : [values];
				}
			}
		}

		return Object.assign({searchCount: this.searchCount}, this.params, this.extraParams);
	}

	processResults(result) {
		// if more than one requests were performed in a rapid sequence, we process only the last one
		if (result.searchCount === this.searchCount && 'content' in result) {
			const opts = this.options;
			const parser = new DOMParser();
			const resultDoc = parser.parseFromString(result.content.trim(), 'text/html');
			const newSearchControls = resultDoc.querySelector(this.dataSelector(searchControlsAttr));
			const newSearchResults = resultDoc.querySelector(this.dataSelector(searchResultsAttr));
			if (newSearchControls) {
				const classPrefix = this.classListParser.prefix;
				morphdom(this.searchControls, newSearchControls, {
					childrenOnly: true,

					getNodeKey: (node) => {
						if (node instanceof HTMLElement) {
							return this.dataAttr(node).get('id', node.id);
						}
						return node.id;
					},

					// Called before a HTMLElement in the from tree is updated. If this function returns false then the element will not be updated.
					onBeforeElUpdated: (fromElement, toElement) => {
						// don't update text inputs to not mess with users typing in the meanwhile
						if (toElement.tagName.toLowerCase() === 'input' && toElement.getAttribute('type') === 'text') {
							return false;
						}
						// if (this.dataAttr(fromElement).has('component') && this.dataAttr(fromElement).get('component') === this.dataAttr(toElement).get('component')) {
						const fromClasses = fromElement.classList.value
							.trim()
							.split(/\s+/)
							.filter((value) => value.indexOf(classPrefix) === 0)
							.map((value) => this.classListParser.removePrefix(value));
						const toClasses = fromElement.classList.value
							.trim()
							.split(/\s+/)
							.filter((value) => value.indexOf(classPrefix) === 0)
							.map((value) => this.classListParser.removePrefix(value));
						const toClassList = this.classList(toElement);
						if (fromClasses.length) {
							console.log('fromClasses', fromClasses);
							toClassList.add(...fromClasses);
						}
						for (const classValue of toClasses) {
							if (toClasses.indexOf(classValue) < 0) {
								toClassList.remove(classValue);
								console.log('removing', classValue);
							}
						}
						return true;
					},
				});
			}
			if (newSearchResults) {
				this.searchResults.replaceWith(newSearchResults);
				this.searchResults = newSearchResults;
			}
			this.classList(this.element).remove(opts.busyClass);
			this.params.page = result.page;
			if (opts.handleQueryString) {
				this.updateQueryString();
			}
		}
	}

	updateQueryString(replace = true) {
		const params = {};
		if (this.params.frontendSearch && this.params.search.trim().length > 0) {
			params.search = this.params.search;
		}
		if ('facets' in this.params) {
			for (const facetName of Object.keys(this.params.facets)) {
				if (this.params.facets[facetName].frontend) {
					params[facetName] = this.params.facets[facetName].values;
				}
			}
		}
		if (this.params.pagination && this.params.page > 1) {
			params.page = this.params.page;
		}
		const qs = queryString.stringify(params, {arrayFormat: 'comma'});
		const url = this.baseUrl + (qs.length ? '?' + qs : '');
		this.history[replace ? 'replace' : 'push'](url, {}, document.title);
	}
}

export default Search;
